import { addonKindsOf, clientTemplateFits } from '@mcpanel/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { clientModsApi } from '../api/client-mod-templates';
import { api3 } from '../api/client-p3';
import type { ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { usePoll } from '../hooks/usePoll';
import { ErrorAlert } from '../components/ui';
import { ClientPackVerificationCard } from '../components/ClientPackVerification';
import { PackVisibility } from './PackVisibility';

export function LauncherSettingsCard({ server }: { server: ServerView }) {
  const { canOperate, notify } = useApp();
  const vanilla = !addonKindsOf(server.software.family).mods;
  const data = usePoll(
    async () => ({
      mods: await api3.serverMods(server.id),
      templates: vanilla ? await clientModsApi.list() : [],
      status: vanilla ? await clientModsApi.status(server.id) : undefined,
      // 管理者用の選択は取れなくても（古い Panel など）、一般用の設定は出す
      admin: vanilla ? await clientModsApi.adminSelected(server.id).catch(() => undefined) : undefined,
    }),
    60000,
    [server.id, server.software.family, server.software.mcVersion],
  );
  const [busy, setBusy] = useState(false);
  // hub 配下のサーバーはランチャーに個別に出ず、クライアント Mod は hub のテンプレートを使う
  const hub = data.data?.status?.hub;
  const save = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      await data.reload();
      notify('ランチャー設定を保存しました');
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card card-body stack">
      <h2 data-search-id="launcher-publish">ランチャーに公開する</h2>
      <ErrorAlert error={data.error} />
      {hub === undefined && (
        <div data-search-id="client-verify">
          <ClientPackVerificationCard
            serverId={server.id}
            repair={
              !vanilla
                ? {
                    href: '/servers/' + encodeURIComponent(server.id) + '?tab=extensions&section=mods',
                    label: 'Mod タブで直せます',
                  }
                : data.data?.status?.templateId
                  ? {
                      href:
                        '/templates?clientTemplate=' +
                        encodeURIComponent(data.data.status.templateId) +
                        '&clientTab=errors&server=' +
                        encodeURIComponent(server.id),
                      label:
                        'テンプレート「' +
                        (data.data.templates.find((entry) => entry.id === data.data?.status?.templateId)?.name ?? '') +
                        '」の「テストとエラー」で直せます',
                    }
                  : undefined
            }
          />
        </div>
      )}
      {data.data && (
        <>
          <PackVisibility
            pack={data.data.mods.pack}
            hub={hub}
            disabled={!canOperate || busy}
            onChange={(adminOnly) => void save(() => api3.setPackAdminOnly(server.id, adminOnly))}
          />
          {hub !== undefined && (
            <p className="alert info small">
              このサーバーは hub 配下です。Shouchan Launcher の一覧には出ず、プレイヤーは{' '}
              <Link to={`/servers/${encodeURIComponent(hub)}?tab=extensions&section=launcher`}>{hub}</Link> から{' '}
              <code>/server {server.id}</code> で移ります。クライアント Mod も {hub}{' '}
              のテンプレートを使うので、ここでは選びません。
            </p>
          )}
          {vanilla && hub === undefined && (
            <>
              <p className="alert info small">
                ここで選ぶのはクライアント Mod（プレイヤーのランチャーだけに入る
                Mod）です。サーバーには入らないので、バニラやプラグインのサーバーでも使えます。
              </p>
              <div className="field">
                <label htmlFor="client-template">クライアント Mod テンプレート</label>
                <select
                  data-search-id="client-template"
                  id="client-template"
                  className="input"
                  value={data.data.status?.templateId ?? ''}
                  disabled={!canOperate || busy}
                  onChange={(event) => void save(() => clientModsApi.select(server.id, event.target.value || null))}
                >
                  <option value="">なし（通常の Minecraft）</option>
                  {data.data.templates
                    .filter(
                      (template) =>
                        clientTemplateFits(template, server.software.mcVersion) ||
                        template.id === data.data?.status?.templateId,
                    )
                    .map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                        {template.mcVersion ? `（${template.mcVersion}）` : ''}
                        {clientTemplateFits(template, server.software.mcVersion) ? '' : '・この版では使えません'}
                      </option>
                    ))}
                </select>
              </div>
              {data.data.status?.reason && <p className="alert warn small">{data.data.status.reason}</p>}
              {data.data.admin && (
                <div className="field">
                  <label htmlFor="admin-client-template">管理者用のクライアント Mod テンプレート</label>
                  <select
                    data-search-id="admin-client-template"
                    id="admin-client-template"
                    className="input"
                    value={data.data.admin?.templateId ?? ''}
                    disabled={!canOperate || busy}
                    onChange={(event) =>
                      void save(() => clientModsApi.selectAdmin(server.id, event.target.value || null))
                    }
                  >
                    <option value="">なし（管理者も一般と同じ）</option>
                    {data.data.templates
                      .filter(
                        (template) =>
                          clientTemplateFits(template, server.software.mcVersion) ||
                          template.id === data.data?.admin?.templateId,
                      )
                      .map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                          {template.mcVersion ? `（${template.mcVersion}）` : ''}
                        </option>
                      ))}
                  </select>
                  <p className="small muted">
                    ランチャーでパネルにログインしたオーナー・管理者にだけ、「{server.name}
                    （管理者）」として別の構成で出ます （例: WorldEdit の表示や地図の
                    Mod）。一般の人には見えません。起動テストはしないので、入れる Mod
                    は一般用のテンプレートで試してから選んでください。
                  </p>
                </div>
              )}
              <p className="small muted">
                テンプレートを選ぶと、対応する Fabric と Mod をランチャーが入れます。選べるのは、このサーバーと同じ版（
                {server.software.mcVersion}）のテンプレートだけです。
              </p>
            </>
          )}
        </>
      )}
    </section>
  );
}
